import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  dropdownClassName?: string;
  optionClassName?: string;
  selectedOptionClassName?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className = '',
  dropdownClassName = '',
  optionClassName = '',
  selectedOptionClassName = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const baseClassName = `
    appearance-none relative w-full pl-4 pr-10 py-2.5 rounded-xl font-semibold text-sm
    shadow-md hover:shadow-lg transition-all duration-200
    cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2
    ${className}
  `;

  const dropdownBaseClassName = `
    absolute z-50 min-w-full w-max mt-2 bg-white rounded-xl shadow-2xl border border-gray-200
    max-h-64 overflow-auto
    ${dropdownClassName}
  `;

  const optionBaseClassName = `
    px-4 py-3 cursor-pointer transition-all duration-150
    hover:bg-gray-50 flex items-center justify-between whitespace-nowrap
    ${optionClassName}
  `;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={baseClassName}
      >
        <span className="block text-left whitespace-nowrap overflow-hidden text-ellipsis pr-2">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className={dropdownBaseClassName}>
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <div
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`
                  ${optionBaseClassName}
                  ${isSelected ? `bg-gray-50 font-semibold ${selectedOptionClassName}` : 'font-medium text-gray-700'}
                `}
              >
                <span className="flex-1">{option.label}</span>
                {isSelected && (
                  <Check className="w-5 h-5 text-gray-900 flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
